import Immutable from 'immutable';
import _ from 'lodash';
import { selectAbbreviationsData } from 'modules/Abbreviations/store/selectors';
import { confirmLexicalStateInTextComponent } from 'modules/draftjs2lexical';
import * as lexical from 'modules/Lexical/helpers';
import { processReferenceCitations } from 'modules/Lexical/sagas/processReferenceCitations';
import * as lexicalRelationStyles from 'modules/Lexical/TextRelationStyles';
import { batchActions } from 'redux-batched-actions';
import { call, put, select } from 'redux-saga/effects';
import guid from 'uuid';
import { TextStatusType } from 'const';
import { setLastEditedLayoutId } from 'containers/App/actions';
import {
  createBrandStylesByRelationId,
  createFlatColorsByRelationId,
  createFlatFontsByRelationId,
  reusableLayoutsRelationIds as reusableLayoutsRelationIdsSelector,
} from 'containers/Common/selectors';
import { addDocuments, setDocument, updateDocument } from 'containers/Documents/actions';
import * as documentsSelectors from 'containers/Documents/selectors';
import { textComponents as textComponentsSelector } from 'containers/ProjectPanel/selectors';
import { updateRelation, updateRelations } from 'containers/Relations/actions';
import { saveAppState } from 'containers/UndoRedoControl/actions';
import { textComponentFactory } from 'factories/document/textComponentFactory';
import * as Models from 'models';
import { handleSagaError } from 'services/handleError';
import { isTextComponent } from 'utils/entityType';
import { findDuplicateId } from 'utils/findDocumentDuplicateId';
import { getChangedStylesToBeKept } from 'utils/styles/getStylesToBeKept';
import { Action, Payload } from '../models';
import { addTextFromAssetsCollection } from '../services/addTextFromAssetsCollection';

const getRelationStylesCreator = (
  sectionStyles: Immutable.List<string>,
  relationId: string,
  stylesToBeKept: Partial<Models.CommonStyles>,
) => function* (): Generator<unknown, Models.TextRelationStyles> {

  const withBrandStyle: Models.TextRelationStyles = (yield call(
    lexicalRelationStyles.useDefaultBrandStyleOnRelation,
    sectionStyles,
    relationId,
  )) as ReturnTypeSaga<typeof lexicalRelationStyles.useDefaultBrandStyleOnRelation>;

  return { ...withBrandStyle, ...stylesToBeKept };
};

// TODO: split this logic on several independent sagas (e.g., dropComponentFromAssetCollection, dropReferenceCitation, etc.)
export function* addTextToCell(action: Action.IAddTextToCell): Generator {
  try {
    const actions: Models.IAction[] = [];
    const {
      component: originalComponent,
      layoutRelations,
      relationId,
      sectionStyles,
      layoutId,
      isDrop,
    } = action.payload as Payload.IAddTextToCell;
    if (isDrop) {
      actions.push(saveAppState());
    }

    const compnentRelation = layoutRelations.get(relationId);
    const componentRelationStyles = isTextComponent(compnentRelation) ? compnentRelation.get('styles')?.toJS() : null;
    const component = confirmLexicalStateInTextComponent(
      originalComponent,
      componentRelationStyles,
    );

    const relation = layoutRelations.get(relationId) as Models.RegularRelationMap<Models.TextRelationStyles>;
    const styles = (yield select(createBrandStylesByRelationId(relationId))) as ReturnType<typeof createBrandStylesByRelationId>;
    const colors = (yield select(createFlatColorsByRelationId(relationId))) as ReturnType<typeof createFlatColorsByRelationId>;
    const fonts = (yield select(createFlatFontsByRelationId(relationId))) as ReturnType<typeof createFlatFontsByRelationId>;
    const changedStylesToBeKept = getChangedStylesToBeKept(relation, styles, colors, fonts);

    const stylesToBeKept = {
      ...changedStylesToBeKept,
      brandStyleChanged: _.some(Models.TextBrandStyleField, styleField => changedStylesToBeKept[styleField]),
    };
    const createRelationStyles = getRelationStylesCreator(
      sectionStyles,
      relationId,
      stylesToBeKept,
    );

    let updatedComponent = component;
    const updatedRelations = layoutRelations.toJS() as Models.Relations;
    let updatedRelation = updatedRelations[relationId] as Models.RegularRelation<Models.TextRelationStyles>;

    const {
      updatedTextComponent,
      newReferenceCitationDocuments,
    } = (yield call<typeof processReferenceCitations>(
      processReferenceCitations,
      updatedComponent,
    )) as NonNullable<ReturnTypeSaga<typeof processReferenceCitations>>;

    updatedComponent = updatedTextComponent;
    newReferenceCitationDocuments.forEach(newReferenceCitationDocument =>
      actions.push(setDocument(newReferenceCitationDocument)));

    updatedRelation = {
      ...updatedRelation,
      styles: lexicalRelationStyles.resetInlineStylesOnRelation(updatedRelation.styles),
    };

    const textsForDuplicatesChecking: ReturnTypeSaga<typeof textComponentsSelector> = yield select(textComponentsSelector);
    const textComponents: ReturnTypeSaga<typeof documentsSelectors.textComponentsForAssetsPanel> = yield select(
      documentsSelectors.textComponentsForAssetsPanel,
    );
    const documents: ReturnTypeSaga<typeof documentsSelectors.documents> = yield select(documentsSelectors.documents);
    const reusableLayoutsRelationIds: ReturnTypeSaga<typeof reusableLayoutsRelationIdsSelector> = yield select(reusableLayoutsRelationIdsSelector);
    const assetsCollectionsDocuments: ReturnTypeSaga<typeof documentsSelectors.assetsCollectionsDocuments> = yield select(
      documentsSelectors.assetsCollectionsDocuments,
    );

    const textComponent = component.toJS() as Models.TextComponent;
    const { status, entityType, id: componentId } = textComponent;
    const documentExists = Boolean(documents.get(componentId));
    const isAssetCollectionsDocument = !!assetsCollectionsDocuments.get(componentId);
    const isRelationInsideReusableLayout = reusableLayoutsRelationIds.includes(relationId);
    const duplicateId = findDuplicateId(textComponent, textsForDuplicatesChecking, lexical.getCleanTextLexical);

    if (documentExists) {
      if (status && status.includes(TextStatusType.EDIT)) {
        // document edited via editor
        let updatedTextComponent = lexical.updateReferenceCitationsOnComponent(updatedComponent);
        // TODO: re-check if status is required in store, probably remove it from store
        updatedTextComponent = updatedTextComponent.set('status', null);
        updatedRelation = {
          ...updatedRelation,
          id: relationId,
          entityType,
          documentId: componentId,
          styles: lexicalRelationStyles.resetInlineStylesOnRelation(updatedRelation.styles),
        };

        actions.push(setDocument(updatedTextComponent));
      } else {
        // document dropped from Text section
        let documentId = componentId;

        if (isRelationInsideReusableLayout) {
          // should we check for duplicates within RL?
          documentId = guid();
          actions.push(setDocument(updatedComponent.set('id', documentId)));
        }

        updatedRelation = {
          id: relationId,
          entityType,
          documentId,
          styles: (yield call(createRelationStyles)) as Models.TextRelationStyles,
        };
      }
    } else if (duplicateId) {
      // document dropped: use existing duplicate instead of new created
      const duplicate = textComponents.get(duplicateId);
      const lexicalState = updatedComponent.get('lexicalState');
      if (lexicalState && lexical.hasTextAbbreviations(lexicalState)) {
        // if there is abbreviations in text that we trying to add we should add these to existing document
        const abbreviationsData = (yield select(selectAbbreviationsData)) as ReturnType<typeof selectAbbreviationsData>;
        const newLexicalState = lexical.removeTextAbbreviationsIfNotPresent(abbreviationsData.toJS(), lexicalState);
        actions.push(updateDocument(duplicate.set('lexicalState', newLexicalState)));
      }

      updatedRelation = {
        id: relationId,
        entityType,
        documentId: duplicateId,
        styles: (yield call(createRelationStyles)) as Models.TextRelationStyles,
      };
      // document dropped from Story Card or Text Collection
    } else if (isAssetCollectionsDocument) {
      // pass the document through the factory to make sure that the document will match its interface
      // TBC: Currently we pick only priority text from storyCard. Should we pick both layers together?
      let newOnlineDocument = Immutable.fromJS(textComponentFactory(updatedComponent.toJS())) as Models.TextComponentMap;
      newOnlineDocument = newOnlineDocument.set('id', guid()).set('status', TextStatusType.COPY);

      const storyCardsAndMagicFormsDocuments = (yield select(
        documentsSelectors.storyCardsAndMagicFormsDocuments,
      )) as ReturnTypeSaga<typeof documentsSelectors.storyCardsAndMagicFormsDocuments>;

      const resultAddText = (yield call(
        addTextFromAssetsCollection,
        newOnlineDocument.toJS() as Models.TextComponent,
        storyCardsAndMagicFormsDocuments,
      )) as ReturnTypeSaga<typeof addTextFromAssetsCollection>;
      const { textComponent: _textComponent, referenceCitations } = resultAddText;
      if (!_.isEmpty(referenceCitations)) {
        actions.push(addDocuments(referenceCitations));
      }
      actions.push(setDocument(_textComponent));

      updatedRelation = {
        id: relationId,
        entityType,
        documentId: newOnlineDocument.get('id'),
        styles: (yield call(createRelationStyles)) as Models.TextRelationStyles,
      };
    } else {
      // document created via editor
      const newOnlineDocument = Immutable.fromJS(textComponentFactory({
        text: updatedComponent.get('text'), // DEPRECATED
        name: updatedComponent.get('name'),
        rawContent: updatedComponent.get('rawContent'),
        lexicalState: updatedComponent.get('lexicalState'),
        language: updatedComponent.get('language').toJS(),
        country: updatedComponent.get('country').toJS(),
        hasProjectMetadataToken: updatedComponent.get('hasProjectMetadataToken'),
      })) as Models.TextComponentMap;

      updatedRelation = {
        ...updatedRelation,
        id: relationId,
        entityType,
        documentId: newOnlineDocument.get('id'),
        styles: lexicalRelationStyles.resetInlineStylesOnRelation(updatedRelation.styles),
      };

      actions.push(setDocument(newOnlineDocument));
    }

    delete updatedRelations[updatedRelation.id];
    actions.push(
      setLastEditedLayoutId(layoutId),
      updateRelation(updatedRelation, { resetLayeredData: updatedRelation.entityType !== relation.get('entityType') }),
      updateRelations(updatedRelations),
    );

    yield put(batchActions(actions));
  } catch (error) {
    yield call(handleSagaError, error, 'ArtboardCell.addTextToCell', 'AddTextToCell');
  }
}
